import { RiArrowUpSLine } from '@remixicon/react';
import type { ReactNode } from 'react';
import { Button, Disclosure, DisclosurePanel } from 'react-aria-components';
import { cn } from './utils';

export type AgCollapsibleCardProps = {
  /** Title displayed in the header */
  title: string;
  /** Content to render inside the collapsible area */
  children: ReactNode;
  /** Whether the card starts expanded (default: true) */
  defaultExpanded?: boolean;
  /** Optional className for the outer container */
  className?: string;
  /** Optional content rendered inline next to the title (e.g. an external link icon) */
  titleContent?: ReactNode;
  /** Optional header actions (buttons, etc.) rendered on the right side of the header */
  headerActions?: ReactNode;
  /** Called once whenever the card transitions between collapsed and expanded */
  onExpandedChange?: (isExpanded: boolean) => void;
};

export function AgCollapsibleCard({
  title,
  children,
  defaultExpanded = true,
  className,
  titleContent,
  headerActions,
  onExpandedChange,
}: AgCollapsibleCardProps) {
  return (
    <Disclosure
      defaultExpanded={defaultExpanded}
      onExpandedChange={onExpandedChange}
      className={cn('rounded-lg border border-neutral-5 bg-white', className)}
    >
      {({ isExpanded }) => (
        <>
          <div className={cn('flex w-full items-center justify-between px-6', isExpanded ? 'pt-6 pb-4' : 'py-6')}>
            <div className="flex items-center gap-2">
              <Button
                slot="trigger"
                className="flex items-center gap-2 cursor-pointer focus:outline-none hover:opacity-80 transition-opacity"
              >
                <RiArrowUpSLine
                  size={20}
                  className={cn('text-neutral-9 transition-transform duration-200', !isExpanded && 'rotate-180')}
                />
                <span className="text-14 font-medium text-neutral-12">{title}</span>
              </Button>
              {titleContent}
            </div>
            {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
          </div>
          <DisclosurePanel className={cn('px-6', isExpanded && 'pb-6')}>{children}</DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
